import React from "react";
import { Formik } from "formik";
import { Drawer, Grid, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import IconButtonIcons from "../../Common/IconButtonIcons";
import Button from "../../Common/Button";
import Input from "../../Common/Input";
import StyledTextarea from "../../Common/StyledTextarea";
import AutoCompleteSelect from "../../Common/AutoCompleteSelect";
import { useDispatch } from "react-redux";
import { setSnackBar } from "../../../store/common/commonSlice";
import { GalleryFolderValidation } from "./GalleryFolderValidation";
import {
    useAddNewGalleryFolderMutation,
    useUpdateGalleryFolderMutation,
} from "../../../store/masters/mastersApis";
import { useGetActivityDropdownQuery } from "../../../store/activity/activityApis";

const defaultInitialValues = {
    name: "",
    activity_id: "",
    description: "",
};

export const GalleryFolderDrawer = ({
    show,
    close,
    formType = "Add",
    initialValues = defaultInitialValues,
}) => {
    const dispatch = useDispatch();
    const [addNewGalleryFolder, { isLoading: addLoading }] =
        useAddNewGalleryFolderMutation();
    const [updateGalleryFolder, { isLoading: updateLoading }] =
        useUpdateGalleryFolderMutation();
    const { data: activityResponse } = useGetActivityDropdownQuery({}, { skip: !show });
    const activityOptions = (activityResponse || []).map((activity) => ({
        value: activity?.value || activity?._id || "",
        label: activity?.label || activity?.name || "",
    }));

    const onFormSubmit = async (values) => {
        try {
            const payload = {
                name: values?.name?.trim(),
                activity_id: values?.activity_id,
                description: values?.description || "",
            };

            if (formType === "Edit" && initialValues?._id) {
                await updateGalleryFolder({
                    _id: initialValues._id,
                    ...payload,
                }).unwrap();
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Folder updated successfully",
                        severity: "success",
                    }),
                );
            } else {
                await addNewGalleryFolder(payload).unwrap();
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Folder created successfully",
                        severity: "success",
                    }),
                );
            }

            close();
        } catch (error) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: error?.data?.message || error.message,
                    severity: "error",
                }),
            );
        }
    };

    return (
        <Formik
            initialValues={{ ...defaultInitialValues, ...initialValues }}
            validationSchema={GalleryFolderValidation}
            onSubmit={onFormSubmit}
            enableReinitialize
        >
            {({ handleChange, handleSubmit, values, errors, setFieldValue }) => (
                <Drawer
                    anchor="right"
                    open={show}
                    PaperProps={{
                        sx: { width: { xs: "100%", md: "40%", sm: "60%", lg: "40%" } },
                    }}
                    onClose={close}
                >
                    <Grid sx={{ display: "flex" }} direction="column" width="100%" height="100%">
                        <Grid
                            container
                            flex={0}
                            px={1}
                            py={1}
                            borderBottom={1}
                            borderColor={"rgba(5, 5, 5, 0.06)"}
                        >
                            <Grid item alignSelf="center">
                                <IconButtonIcons
                                    color="default"
                                    title="Close"
                                    IconComponent={CloseIcon}
                                    onClick={close}
                                />
                            </Grid>
                            <Grid item alignSelf="center">
                                <Typography variant="h6">
                                    {formType} Gallery Folder
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid flex={1} px={2} py={4} overflow="auto">
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <AutoCompleteSelect
                                        id="folder_activity"
                                        options={activityOptions}
                                        label="Linked Activity"
                                        onChange={(e, val) =>
                                            setFieldValue("activity_id", val?.value || "")
                                        }
                                        value={
                                            activityOptions.find(
                                                (option) => option.value === values?.activity_id,
                                            ) || null
                                        }
                                        error={Boolean(errors.activity_id)}
                                        helperText={errors.activity_id}
                                        name="activity_id"
                                        keyname="label"
                                        disableCloseOnSelect={false}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Input
                                        id="folder_name"
                                        name="name"
                                        label="Folder Name"
                                        onChange={handleChange("name")}
                                        value={values?.name || ""}
                                        error={Boolean(errors.name)}
                                        helperText={errors.name}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <StyledTextarea
                                        id="folder_description"
                                        name="description"
                                        placeholder="Folder Description"
                                        onChange={handleChange("description")}
                                        value={values?.description || ""}
                                        error={Boolean(errors.description)}
                                        helperText={errors.description}
                                        fullWidth
                                        minRows={4}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid
                            flexShrink={0}
                            borderTop={1}
                            borderColor={"rgba(152, 188, 252, 0.16)"}
                            sx={{ padding: "8px 16px" }}
                        >
                            <Grid sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Button size="large" color="warning" type="button" onClick={close}>
                                        Cancel
                                    </Button>
                                    <Button
                                        size="large"
                                        type="submit"
                                        loading={addLoading || updateLoading}
                                        onClick={handleSubmit}
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Drawer>
            )}
        </Formik>
    );
};
